import React, { useState, useEffect } from 'react';
import '../../styles/paynow-qr-modal.css';

const PayNowQRModal = ({
  isOpen,
  onClose,
  qrCodeData,
  qrCodeUrl,
  isDynamic,
  fallback,
  fallbackReason,
  amount,
  reference,
  transactionId,
  salonId,
  onPaymentVerified,
  onTryAgain,
  onCancelTransaction,
  isGenerating
}) => {
  const [statusMessage, setStatusMessage] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [countdown, setCountdown] = useState(1800); // 30 minutes in seconds

  useEffect(() => {
    if (!isOpen) return;

    // Reset state when modal opens
    setPaymentCompleted(false);
    setStatusMessage('');
    setCountdown(1800);

    // Countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setStatusMessage('QR code has expired. Please click "Try Again" to generate a new one.');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen]);

  // Format countdown as MM:SS
  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePaymentReceived = async () => {
    setIsVerifying(true);
    setStatusMessage('Verifying payment...');

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(
        `${API_BASE_URL}/api/paynow/verify-payment`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ transactionId, salonId, reference })
        }
      );

      const data = await response.json();

      if (data.success) {
        setPaymentCompleted(true);
        setStatusMessage('Payment verified successfully!');

        if (onPaymentVerified) {
          setTimeout(() => {
            onPaymentVerified(transactionId);
          }, 1500);
        }
      } else {
        setStatusMessage(data.message || 'Failed to verify payment. You can try again or cancel.');
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      setStatusMessage('Error verifying payment. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleClose = () => {
    if (!paymentCompleted) {
      const confirmClose = window.confirm(
        'Payment has not been verified yet. Are you sure you want to close?'
      );
      if (!confirmClose) return;
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="paynow-qr-modal-overlay" onClick={handleClose}>
      <div className="paynow-qr-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="paynow-qr-modal-header">
          <h2>PayNow Payment</h2>
          <button className="close-button" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="paynow-qr-modal-body">
          {paymentCompleted ? (
            <div className="payment-success">
              <div className="success-icon">&#10003;</div>
              <h3>Payment Successful!</h3>
              <p>The transaction has been marked as paid.</p>
            </div>
          ) : (
            <>
              <div className="qr-code-section">
                <h3>Scan QR Code to Pay</h3>
                <p className="qr-instruction">
                  Open your banking app and scan this QR code to complete the payment
                </p>

                {isDynamic && !fallback && (
                  <span className="qr-type-badge dynamic">Dynamic QR - Amount Pre-filled</span>
                )}
                {fallback && (
                  <span className="qr-type-badge static">Static QR</span>
                )}

                <div className="qr-code-container">
                  {isGenerating ? (
                    <div className="qr-loading">
                      <div className="spinner"></div>
                      <p>Generating QR Code...</p>
                    </div>
                  ) : isDynamic && qrCodeData ? (
                    <img
                      src={`data:image/png;base64,${qrCodeData}`}
                      alt="PayNow QR Code"
                      className="qr-code-image dynamic-qr"
                    />
                  ) : qrCodeUrl ? (
                    <img src={qrCodeUrl} alt="PayNow QR Code" className="qr-code-image static-qr" />
                  ) : (
                    <div className="qr-code-placeholder">
                      <p>QR Code not available</p>
                      <small>Please configure PayNow settings in Super Admin</small>
                    </div>
                  )}
                </div>

                {fallback && (
                  <div className="fallback-warning">
                    <strong>Note:</strong> Dynamic QR unavailable. Please enter the amount
                    (SGD {parseFloat(amount).toFixed(2)}) manually when scanning this QR code.
                    {fallbackReason && <div className="fallback-reason">Reason: {fallbackReason}</div>}
                  </div>
                )}

                <div className="payment-details">
                  <div className="payment-detail-row">
                    <span className="label">Amount:</span>
                    <span className="value amount-value">SGD ${parseFloat(amount).toFixed(2)}</span>
                  </div>
                  {reference && (
                    <div className="payment-detail-row">
                      <span className="label">Reference:</span>
                      <span className="value reference-value">{reference}</span>
                    </div>
                  )}
                  <div className="payment-detail-row">
                    <span className="label">Valid for:</span>
                    <span className={`value countdown-value ${countdown < 60 ? 'expiring' : ''}`}>
                      {formatCountdown(countdown)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="instructions-section">
                <h4>How to pay with PayNow:</h4>
                <ol className="payment-instructions">
                  <li>Open your mobile banking app (DBS/POSB, OCBC, UOB, etc.)</li>
                  <li>Select "PayNow" or "Scan & Pay" option</li>
                  <li>Scan the QR code displayed above</li>
                  {isDynamic && !fallback ? (
                    <li>The amount (SGD {parseFloat(amount).toFixed(2)}) is pre-filled</li>
                  ) : (
                    <li>Enter the amount: SGD {parseFloat(amount).toFixed(2)}</li>
                  )}
                  <li>Complete the payment in your banking app</li>
                  <li>Click "Payment Received" below after confirming payment in your banking app</li>
                </ol>
              </div>

              {statusMessage && (
                <div className={`status-message ${paymentCompleted ? 'success' : 'info'}`}>
                  {statusMessage}
                </div>
              )}
            </>
          )}
        </div>

        <div className="paynow-qr-modal-footer">
          {!paymentCompleted && (
            <>
              <button
                className="btn-danger-secondary"
                onClick={onCancelTransaction}
                disabled={isVerifying || isGenerating}
              >
                Cancel Transaction
              </button>
              <button
                className="btn-secondary"
                onClick={onTryAgain}
                disabled={isVerifying || isGenerating}
              >
                Try Again
              </button>
              <button
                className="btn-primary"
                onClick={handlePaymentReceived}
                disabled={isVerifying || countdown === 0 || isGenerating}
              >
                {isVerifying ? 'Verifying...' : 'Payment Received'}
              </button>
            </>
          )}
          {paymentCompleted && (
            <button className="btn-primary" onClick={onClose}>
              Close
            </button>
          )}
        </div>

        {!paymentCompleted && (
          <div className="modal-footer-note">
            <small>
              After the customer scans and completes payment in their banking app,
              confirm by clicking "Payment Received" to update the transaction.
            </small>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayNowQRModal;
